import { Component, createRef } from "preact";
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { frontloadConnect } from 'react-frontload';
import _ from 'lodash';
import { helpers, HOMEPAGE_ORIGIN, Login } from "@cargo/common";
import { actions } from "../actions";
import withStore from '../withStore';
import Routes from "../routes";
import ScrollContextProvider from "./page/scroll-element";
import GlobalEventsHandler from "./global-events-handler";
import windowInfo from "./window-info";
import Password from './page/password';
import FrontendAlertProxy from './frontend-alert-proxy';
import NotificationPage from './notification-page';
import loadFonts from '@cargo/font-loader';
import { CustomElementHost } from './page/register'
import "./page/media-item";
import "./page/gallery/layouts/grid";
import "./page/gallery/layouts/slideshow";
import "./page/gallery/layouts/columnized";
import "./page/gallery/layouts/freeform";
import "./page/gallery/layouts/justify";
import "./page/columns";
import "./page/audio-player";
import "./page/digital-clock";
import "./page/shop-product";
import "./page/marquee";
import "./page/text-icon-element"
import EditorOverlayController from './overlay/editor-overlay-controller';
import { getMobileOffsetsString } from "../helpers";

let frontLoadComplete = false;
let firstFontLoadComplete = false;
let backdropLoadComplete = true;

const dispatchFirstRenderEvent = (forceInitialRenderEvent = false) => {

	if(forceInitialRenderEvent || (frontLoadComplete && firstFontLoadComplete && backdropLoadComplete)) {
		setTimeout(() => {
			// backdrop loads might initiate at a later point, check again 
			// here in case backdrops started loading in the mean time
			if(backdropLoadComplete) {
				window.dispatchEvent(new CustomEvent('initial-content-visible', {
					detail: {
						screenshots: {
							hiddenIds: [ 'dl-duplicate-site' ],
						}
					}
				}));
			}
		}, 1500);
	}

}

const frontload = async props => { 

	const frontloadPromises = [];

	if(props.hasSitePackage !== true) {

		frontloadPromises.push( 
			props.fetchSitePackage()
		);

	}

	// load fonts when server rendering this site
	if(helpers.isServer && props.fonts && _.isArray(props.fonts)) {

		// do not load Google Fonts in a server thread. They need to be requested by the
		// client so their api can sniff browser support.
		const fontsToLoad = [...props.fonts].filter(font => font.provider !== 'google');

		const fontLoadPromise = loadFonts({
			"context": "server",
			"families": fontsToLoad
		});

		fontLoadPromise.then(results => {

			props.updateFrontendState({
				fontsLoaded: _.uniq(props.fontsLoaded.concat(fontsToLoad.map(font => font.family))),
				serverRenderedFontCSS: results.filter(result => result ?? false)
			});

		})

		frontloadPromises.push(fontLoadPromise);

	}

	await Promise.allSettled(frontloadPromises);

	frontLoadComplete = true;

	dispatchFirstRenderEvent();
	
}

class App extends Component {

	constructor(props) {

		super(props);

		this.state = {
			checkedExistingAuth: helpers.isServer ? true : false,
			checkedCookieAuth: helpers.isServer ? true : false,
			mobileOffsetsString: null,
			inheritableFontSizesString: null,
			linkStylesString: null,
		}

		if(!helpers.isServer) {

			import('./quick-view').then(({default: QuickViewComponent}) => {
				this.setState({
					QuickView: QuickViewComponent
				})
			})

			if(props.inAdminFrame === true) {
				const head  = document.getElementsByTagName('head')[0];
				const link  = document.createElement('link');
				link.rel  = 'stylesheet';
				link.type = 'text/css';
				link.href = PUBLIC_URL + '/css/front-end/frontend-interface.css';
				link.id = 'frontend-interface'
				head.appendChild(link);
			}

			// set mobile class if needed
			this.setMobileClass(this.props.isMobile);

			let backdropLoadStarts = 0;
			let backdropLoadCompletes = 0;

			window.addEventListener('backdrop-load-start', () => {

				backdropLoadComplete = false;
				backdropLoadStarts++;

			})

			window.addEventListener('backdrop-load-complete', () => {

				backdropLoadCompletes++;

				if(backdropLoadCompletes === backdropLoadStarts) {
					backdropLoadComplete = true;
					dispatchFirstRenderEvent()
				}

			});

		}

		this.globalStyleRef = createRef();
		this.customHTMLRef = createRef();

		this.scrollingElement = {current: helpers.isServer ? null : window};

		this.setWindowTitle();
	}

	setWindowTitle = () => {

		if(helpers.isServer) {
			return;
		}

		const state = this.props.store.getState();
		let activePageOrSet = undefined;
		let title = "";

		if (
			this.props.activePID
			&& this.props.activePID !== state.site.homepage_id
			&& this.props.activePID !== state.site.mobile_homepage_id
			&& this.props.activePID !== 'root'
		) {
			if (state.pages.byId[this.props.activePID]) {
				activePageOrSet = state.pages.byId[this.props.activePID];
			} else if (state.sets.byId[this.props.activePID]) {
				activePageOrSet = state.sets.byId[this.props.activePID];
			}
		}

		if (this.props.website_title) {
			const pageTitle = (activePageOrSet?.title) ? `${activePageOrSet.title} — ` : '';
			title = `${pageTitle}${this.props.website_title}`;
		} 

		if(title !== document.title) {
			document.title = title;
		}

	}

	render() {

		if(this.props.hasSitePackage !== true) {
			return null;
		}

		if(
			this.props.access_level === 'password'
			|| this.props.access_level === 'private'
		) {

			let jsx = null;

			if(this.props.hasAuth && this.state.checkedExistingAuth === false) {

				// we have auth but the page is private. Attempt to fetch the site package
				// with the pre-existing auth data first
				if(!this.fetchingSitePackage) {
					
					this.fetchingSitePackage = true;

					this.props.fetchSitePackage()
						.finally(() => {
							delete this.fetchingSitePackage;

							this.setState({
								checkedExistingAuth: true
							});

						})
				}

			} else if(this.state.checkedCookieAuth === false) {

				// else attempt to automatically retrieve token
				if(!this.checkingAuth) {

					this.checkingAuth = true;

					this.props.getAuthToken({
						// add a relatively short timeout
						// because we don't want to get stuck in this mode
						// for too long
						timeout: 1500,
						site_url: this.props.site_url,
						site_id: this.props.site_id
					}).then(() => {

						// got a token, attempt to fetch the package
						// and then mark cookie check as completed.
						this.props.fetchSitePackage()
							.finally(() => {
								delete this.checkingAuth;
								this.setState({
									checkedCookieAuth: true
								});
							})

					}).catch(e => {

						this.setState({
							checkedCookieAuth: true
						});

					}).finally(() => {
						delete this.checkingAuth;
					})

				}

			} else {

				if(this.props.access_level === 'password') {

					jsx =  <Password target="site" onSucces={() => {
						// re-fetch package with new data
						this.props.fetchSitePackage();
					}}/>

				} else if(this.props.access_level === 'private') {

					jsx = <NotificationPage message="This site is private."/>;

				}

				// let the screenshotter know we're done.
				this.onFontLoad(true);

			}

			return <>
				<GlobalEventsHandler>
					{ jsx }
				</GlobalEventsHandler>
			</>

		}
		
		if(
			!helpers.isServer
			// we're not in the admin, which handles its own redirect.
			&& !helpers.isAdminEdit
			// This site has an active domain
			&& this.props.domain_active === true
			// but we are currently not loading the site through that domain
			&& this.props.domain !== window.location.host 
			// not in site preview on u.cargo
			&& !helpers.isSitePreview
		) {

			window.location.hostname = this.props.domain;

			return null;
		}

		// only set custom HTML using dangerouslySetInnerHTML for the initial render. After
		// that we use the method in componentDidUpdate
		const cachedCustomHTML = this.props.customHTML;
		const canDuplicate = this.props.canDuplicate && !helpers.isServer;

		return (
			<>
				{canDuplicate  ? 
					<>
						<a
							id="dl-duplicate-site"
							class="dl-duplicate"
							href={`${HOMEPAGE_ORIGIN}?duplicate=${this.props.site_id}`}
							onClick={e => {

								// we can only check cookie auth on cargo.site
								if(!this.props.domain_active) {

									e.preventDefault();

									// check auth and show inline login if not logged in yet
									const AUTH_PATH = CARGO_ENV !== "production" ?
										'https://dev.cargo.site/accesstoken' :
										'https://cargo.site/accesstoken';

									fetch(AUTH_PATH + `?site_id=${this.props.site_id}`, {
										credentials: "include"
									})
										.then(response => response.json())
										.then(result => {

											if(result?.access_token) {
												// good to go, already logged in
												window.location.href = `${HOMEPAGE_ORIGIN}?duplicate=${this.props.site_id}`;
											} else {

												// no auth, show login
												this.setState({
													loginBeforeDuplicatingSite: true
												});

											}

										}).catch(() => {
											
											// unable to get auth. Show a login window
											this.setState({
												loginBeforeDuplicatingSite: true
											});

										})

									}

							}}
						>
							<svg width="84" height="19" viewBox="0 0 84 19"><path fill="#027aff" d="m0,9.5C0,4.25,3.96,0,8.85,0h66.14c4.89,0,8.85,4.25,8.85,9.5s-3.96,9.5-8.85,9.5H8.85C3.96,19,0,14.75,0,9.5Z"/><path fill="#fff" d="m9.29,4.54h2.47c2.38,0,3.6,1.42,3.6,4.2s-1.21,4.25-3.6,4.25h-2.47V4.54Zm1.22,1.08v6.29h1.11c1.7,0,2.49-1,2.49-3.16s-.8-3.13-2.49-3.13h-1.11Z"/><path fill="#fff" d="m22.23,6.61v6.39h-1.14v-1.11h-.08c-.33.79-1.05,1.23-2.01,1.23-1.39,0-2.14-.83-2.14-2.37v-4.14h1.18v3.86c0,1.09.44,1.61,1.38,1.61s1.63-.69,1.63-1.72v-3.74h1.17Z"/><path fill="#fff" d="m27.47,6.51c1.47,0,2.41,1.05,2.41,2.71v1.17c0,1.66-.94,2.71-2.41,2.71-.93,0-1.59-.4-1.85-1.12h-.11v3.16h-1.21V6.61h1.18v1.01h.11c.3-.73.96-1.11,1.88-1.11Zm-.39,1.04c-.98,0-1.6.69-1.6,1.79v.96c0,1.1.62,1.79,1.6,1.79s1.61-.7,1.61-1.79v-.96c0-1.1-.63-1.79-1.61-1.79Z"/><path fill="#fff" d="m37.19,13h-5.36v-.95h2.14v-6.97h-2.09v-.96h3.26v7.93h2.05v.95Z"/><path fill="#fff" d="m41.91,5.12c-.52,0-.91-.36-.91-.87s.39-.87.91-.87.91.36.91.87-.38.87-.91.87Zm2.7,7.88h-5.36v-.95h2.15v-4.49h-2.1v-.95h3.25v5.44h2.06v.95Z"/><path fill="#fff" d="m51.95,8.67h-1.16c-.11-.74-.69-1.18-1.46-1.18-1.04,0-1.61.73-1.61,2.04v.54c0,1.31.57,2.04,1.61,2.04.79,0,1.35-.43,1.46-1.15h1.16c-.16,1.36-1.12,2.16-2.61,2.16-1.78,0-2.8-1.07-2.8-2.93v-.79c0-1.86,1.03-2.92,2.8-2.92,1.49,0,2.46.8,2.61,2.19Z"/><path fill="#fff" d="m55.93,13.11c-1.22,0-2.03-.77-2.03-1.92s.82-1.8,2.31-1.89l1.68-.09v-.56c0-.73-.46-1.14-1.28-1.14-.68,0-1.17.29-1.32.8h-1.15c.12-1.05,1.12-1.77,2.49-1.77,1.54,0,2.42.77,2.42,2.12v4.37h-1.12v-.93h-.11c-.3.66-.99,1.03-1.89,1.03Zm.36-.98c.91,0,1.59-.6,1.59-1.42v-.67l-1.52.08c-.85.05-1.29.4-1.29,1.02s.47,1,1.22,1Z"/><path fill="#fff" d="m62.9,4.87h1.16v1.78h2.53v.97h-2.51v3.45c0,.63.44.98,1.25.98.3,0,1.08-.01,1.22-.03v.96c-.13.02-1.05.05-1.3.05-1.63,0-2.35-.59-2.35-1.96v-3.45h-1.69v-.97h1.69v-1.78Z"/><path fill="#fff" d="m73.1,11.31h1.12c-.17,1.07-1.22,1.81-2.6,1.81-1.79,0-2.82-1.05-2.82-2.87v-.97c0-1.68,1.1-2.79,2.75-2.79s2.68,1.1,2.68,2.92v.69h-4.28v.29c0,1.08.65,1.77,1.66,1.77.76,0,1.32-.32,1.48-.84Zm-1.55-3.86c-.99,0-1.59.68-1.59,1.82h3.13c0-1.14-.57-1.82-1.54-1.82Z"/></svg>
						</a>
					</>
				:
					<>
						{this.props.showCargoLogo && <a id="cargo-dotsite" class="cargodotsite" href="https://cargo.site" target="_blank">
							<svg width="84" height="19" viewBox="0 0 84 19" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path fillRule="evenodd" clipRule="evenodd" d="M8.85203 0C3.96319 0 0 4.2533 0 9.50005C0 14.7467 3.9632 18.9999 8.85203 18.9999H74.9948C79.8836 18.9999 83.8468 14.7467 83.8468 9.50005C83.8468 4.2533 79.8836 0 74.9948 0H8.85203ZM63.5625 4.05077H61.4194L61.8679 1.92424H64.0111L63.5625 4.05077ZM61.4858 13.3875H59.4756L61.2034 5.28016H63.2136L61.4858 13.3875ZM14.2135 10.53C13.8812 12.4405 12.17 13.6865 10.0435 13.6865C7.83393 13.6865 6.40517 12.2744 6.40517 9.98175C6.40517 7.34024 8.14958 5.01435 11.0237 5.01435C13.2665 5.01435 14.5458 6.39327 14.5291 8.56963H12.5688C12.5521 7.47314 12.0537 6.62586 10.9573 6.62586C9.34575 6.62586 8.39878 8.33704 8.39878 9.94855C8.39878 11.261 9.07994 12.0916 10.1432 12.0916C11.1068 12.0916 11.9208 11.4437 12.1368 10.53H14.2135ZM20.0586 12.4571C19.5602 13.1051 18.7295 13.6865 17.5998 13.6865C16.0381 13.6865 14.9416 12.6067 14.9416 11.2277C14.9416 9.41695 16.4701 8.68593 18.1314 8.51979L19.8924 8.33704C20.5902 8.27059 20.856 7.98816 20.856 7.52298C20.856 6.99135 20.3576 6.52617 19.5103 6.52617C18.5966 6.52617 18.065 7.00797 17.9321 7.6725H15.7889C16.0049 6.09422 17.3506 5.01435 19.4439 5.01435C21.7864 5.01435 23.1653 6.22713 22.6503 8.66931L21.6535 13.3875H19.7928L19.8562 13.1336C19.9044 12.9393 19.9677 12.6844 20.0586 12.4571ZM16.9352 11.1779C16.9352 11.8591 17.417 12.1913 18.0816 12.1913C19.1781 12.1913 20.1084 11.2776 20.341 10.2475L20.5238 9.43355C20.3078 9.61625 19.9755 9.68275 19.4439 9.76585L18.7295 9.88205C17.7161 10.0482 16.9352 10.3472 16.9352 11.1779ZM25.3126 13.3875L26.1267 9.53325C26.542 7.58944 27.5886 6.99135 28.7682 6.99135C29.0174 6.99135 29.2168 7.02458 29.3663 7.05781L29.7982 5.0808C29.6819 5.04757 29.4992 5.01435 29.2832 5.01435C28.087 5.01435 27.2231 5.89486 26.6417 6.74215C26.7912 6.42649 26.8909 6.11084 26.9241 5.89486L27.0404 5.28016H25.0302L23.3024 13.3875H25.3126ZM36.5982 13.3376C36.05 15.946 34.7043 17.0757 32.4282 17.0757C30.202 17.0757 28.9062 15.9127 28.9062 13.7198H30.9496C30.9496 14.8827 31.448 15.4808 32.4947 15.4808C33.4749 15.4808 34.2557 14.9159 34.5215 13.7031L34.7209 12.7728C34.7873 12.4239 34.8538 12.1581 34.9701 11.8757C34.372 12.6067 33.6908 13.0884 32.611 13.0884C30.933 13.0884 29.4544 11.8092 29.4544 9.63285C29.4544 7.1741 31.1656 5.01435 33.4416 5.01435C34.9036 5.01435 35.7177 5.6955 36.0832 6.62586C36.0998 6.29359 36.1663 5.87825 36.2493 5.49614L36.2992 5.28016H38.3094L36.5982 13.3376ZM31.4646 9.54985C31.4646 10.6961 32.1458 11.4769 33.1924 11.4769C34.7209 11.4769 35.6845 9.91535 35.6845 8.48657C35.6845 7.43992 35.0365 6.62586 33.9899 6.62586C32.5113 6.62586 31.4646 8.07123 31.4646 9.54985ZM42.3466 13.6865C45.2207 13.6865 47.0316 11.56 47.0316 8.6527C47.0316 6.50956 45.5696 5.01435 43.3932 5.01435C40.5856 5.01435 38.7082 7.1741 38.7082 10.1146C38.7082 12.2744 40.1868 13.6865 42.3466 13.6865ZM42.4795 12.0916C41.3498 12.0916 40.7019 11.3773 40.7019 10.0814C40.7019 8.38688 41.6488 6.62586 43.2603 6.62586C44.3568 6.62586 45.038 7.32362 45.038 8.6527C45.038 10.3306 44.091 12.0916 42.4795 12.0916ZM50.4596 10.9121L49.9279 13.3875H47.4027L47.9343 10.9121H50.4596ZM55.1411 13.6865C57.251 13.6865 58.6299 12.6067 58.6299 10.9785C58.6299 9.69935 57.6165 9.06805 56.4369 8.68593L55.357 8.33704C54.5596 8.08784 54.3602 7.87187 54.3602 7.45653C54.3602 6.89167 54.8752 6.52617 55.6395 6.52617C56.6196 6.52617 57.2177 7.10765 57.2343 8.07123H59.2612C59.2114 6.16068 57.8657 5.01435 55.7391 5.01435C53.6624 5.01435 52.4164 6.11084 52.4164 7.5396C52.4164 8.66931 53.164 9.43355 54.5097 9.86545L55.4567 10.1645C56.2209 10.3971 56.6861 10.5798 56.6861 11.1447C56.6861 11.7428 56.0714 12.1747 55.1909 12.1747C54.1276 12.1747 53.4631 11.4769 53.4631 10.3805H51.4362C51.4362 12.4239 52.865 13.6865 55.1411 13.6865ZM67.0193 13.537C67.4845 13.537 67.8167 13.4706 68.0327 13.3875L68.365 11.8092C68.1822 11.8591 68.0327 11.8757 67.8167 11.8757C66.8698 11.8757 66.5707 11.3773 66.7535 10.4967L67.5177 6.89167H69.4947L69.827 5.28016H67.8666L68.4813 2.35619H66.471L65.8563 5.28016H64.0289L63.68 6.89167H65.5075L64.7432 10.4137C64.3445 12.2744 65.1087 13.537 67.0193 13.537ZM77.0728 10.9785C76.6741 12.5236 75.2453 13.6865 73.1022 13.6865C70.9756 13.6865 69.414 12.2744 69.414 10.015C69.414 7.34023 71.2082 5.01435 74.0491 5.01435C76.591 5.01435 77.8204 6.82522 77.3386 9.38365L77.2555 9.83225H71.4076C71.3245 11.0118 71.8728 12.0916 73.1354 12.0916C74.1322 12.0916 74.6306 11.6597 74.9463 10.9785H77.0728ZM74.0159 6.52617C72.9194 6.52617 72.0057 7.30701 71.7066 8.37027H75.4945C75.6606 7.19071 75.0792 6.52617 74.0159 6.52617Z" fill="white"/>
							</svg>
						</a>}
					</>
				}

				<customhtml ref={this.customHTMLRef} dangerouslySetInnerHTML={ {__html: cachedCustomHTML} }></customhtml>
				
				<FrontendAlertProxy />
				<GlobalEventsHandler />

				<style ref={this.globalStyleRef} dangerouslySetInnerHTML={{__html: this.props.stylesheet}}></style>
				<style id="mobile-offset-styles" dangerouslySetInnerHTML={{__html: this.state.mobileOffsetsString}}></style>
				<style id="text-style-font-sizes" dangerouslySetInnerHTML={{__html: this.state.inheritableFontSizesString}}></style>
				<style id="link-styles" dangerouslySetInnerHTML={{__html: this.state.linkStylesString}}></style>

				<ScrollContextProvider scrollingElement={this.scrollingElement}>
					<Routes />
					{this.state.QuickView && <this.state.QuickView />}
					{this.props.inAdminFrame && <EditorOverlayController />}
					{this.state.loginBeforeDuplicatingSite && <Login 
						loginToUdotCargoOnly={true}
						canCreateNewAccount={true} 
						animateIn={true}
						canClickout={true}
						templateID={this.props.site_id}
						onClickout={() => {
							this.setState({
								loginBeforeDuplicatingSite: false
							})
						}}
						onLoginSuccess={data => {
							window.location.href = `${HOMEPAGE_ORIGIN}?duplicate=${this.props.site_id}`;
						}
					}/>}
					<CustomElementHost portalHost={helpers.isServer ? null : document.body}></CustomElementHost>
				</ScrollContextProvider>
			</>
		);
	}

	componentDidMount() {

		// load any remaining fonts
		this.loadNewFonts();

		this.inPreviewMode = this.props.inAdminFrame && this.props.adminMode === false;
		this.showHideAdminInterface();

		windowInfo.on('mobile-change', this.onMobileChange);

		// set initial
		this.onMobileChange(windowInfo.data.mobile.active);

		this.updateCartCounter();

	}

	static getDerivedStateFromError(error) {

		return {
			hasError: true
		};

	}

	componentDidCatch(error, errorInfo) {
		
		// log this to some error logging service
		console.error(error);

	}

	onMobileChange = (isMobile) => {
		
		this.props.updateFrontendState({
			isMobile
		});

	}

	onFontLoad = (forceInitialRenderEvent = false) => {

		if(helpers.isServer || firstFontLoadComplete) {
			return;
		}

		firstFontLoadComplete = true;
		document.documentElement.classList.remove('wf-initial-load');
		dispatchFirstRenderEvent(forceInitialRenderEvent);
	}

	componentWillUnmount(){
		windowInfo.off('mobile-change', this.onMobileChange)
	}

	showHideAdminInterface = () => {
		if (this.inPreviewMode) {
			document.querySelector('#frontend-interface')?.setAttribute('disabled', '');
		} else {
			document.querySelector('#frontend-interface')?.removeAttribute('disabled');
		}
	}

	setMobileClass = (state) => {
		if (state) {
			document.body.classList.add('mobile')
			document.documentElement.classList.add('mobile')
		} else {
			document.body.classList.remove('mobile')
			document.documentElement.classList.remove('mobile')
		}
	}

	setEditingClass = (state) => {
		if (state) {
			document.body.classList.add('editing')
		} else {
			document.body.classList.remove('editing')
		}
	}

	loadNewFonts = () => {

		if(!this.props.fonts) {
			return;
		}

		const newFonts = this.props.fonts.filter(font => !this.props.fontsLoaded.includes(font.family));

		if(newFonts && newFonts.length > 0) {

			this.props.updateFrontendState({
				fontsLoaded: _.uniq(this.props.fontsLoaded.concat(newFonts.map(font => font.family)))
			});

			loadFonts({
				families: newFonts
			}).then(() => {
				if(!firstFontLoadComplete) {
					
					document.fonts.ready.then(() => {
						this.onFontLoad();
					});

				}
			});

		} else {

			// Nothing to load, mark font loads as completed.
			document.fonts.ready.then(() => {
				this.onFontLoad();
			});

		}

	}

	componentDidUpdate(prevProps, prevState) {

		if(helpers.isServer) {
			// nothing below should run on the server
			return;
		}

		this.setWindowTitle();

		this.inPreviewMode = this.props.inAdminFrame && this.props.adminMode === false;

		if(typeof this.props.customHTML === "string" && this.props.customHTML !== prevProps.customHTML) {

			if(this.customHTMLRef.current) {

				// scripts won't execute when simply setting innerHTML.
				const newHTML = document.createRange().createContextualFragment(this.props.customHTML)

				this.customHTMLRef.current.innerHTML = '';
				this.customHTMLRef.current.appendChild(newHTML);
			}

		}

		if( this.props.stylesheet !== prevProps.stylesheet ) {
			this.setMobileOffsets();
			this.setInheritableFontSizes();
			this.setLinkStyles();
		}

		if( this.props.fonts !== prevProps.fonts && _.isArray(this.props.fonts) ) {
			this.loadNewFonts();
		}

		if(this.props.inAdminFrame === true) {

			// add a class while in the admin (but not in preview)
			this.setEditingClass(this.props.adminMode);

			if(this.props.adminInitialized !== prevProps.adminInitialized) {
				// when the admin is initialized we have the CSS from the CRDT. Load fonts from it
				this.setMobileOffsets();
				this.setInheritableFontSizes();
				this.setLinkStyles();
			}

		} else {

			// remove 'editing' class out of the admin
			this.setEditingClass(false);

		}

		if(prevProps.isMobile !== this.props.isMobile) {
			this.setMobileClass(this.props.isMobile);
			this.setMobileOffsets();
			this.setInheritableFontSizes();
			this.setLinkStyles();
		}

		if(this.props.itemsInCart !== prevProps.itemsInCart) {
			this.updateCartCounter();
		}

		this.showHideAdminInterface()
	}

	updateCartCounter = () => {

		document.documentElement.style.setProperty("--cart-item-count", this.props.itemsInCart || "");
		// control visibility of the cart item counter
		document.documentElement.style.setProperty("--cart-item-count-display", this.props.itemsInCart ? 'inline' : "");

	}

	setLinkStyles = () => {
		if(helpers.isServer) {
			return;
		}

		if (!this.props.stylesheet) {
			return;
		}

		const isolatedStyle = this.getIsolatedStyleSheet();
		isolatedStyle.innerHTML = this.props.stylesheet;
		let stylesheet = isolatedStyle.sheet;

		if(!stylesheet) {return null;}

		let linkStylesString = '';

		// first loop finding all text style classes
		const textStyleSelectors = [];

		_.each(stylesheet.rules, rule => {

			if(rule?.style?.getPropertyValue('--text-style')) {
				textStyleSelectors.push(rule.selectorText);
			}

		});

		// loop again and handle text style selectors with link styles in them
		_.each(stylesheet.rules, rule => {

			textStyleSelectors?.forEach(selector => {

				if(rule.selectorText?.startsWith(selector) && rule.selectorText?.includes(' a')) {
					const flippedSelector = rule.selectorText?.replace(/^(.*)\s(\a(?:[:.]\S*)?)$/i, (match, group1, group2) => {
						return group2 + group1;
					})

					// if something changed,
					if(rule.selectorText !== flippedSelector) {
						linkStylesString += flippedSelector + ' { ' + rule.style?.cssText + ' } ';
					}

				}

			})

		});

		this.setState({linkStylesString: linkStylesString});

	}

	setInheritableFontSizes = () => {
		if(helpers.isServer) {
			return;
		}

		if (!this.props.stylesheet) {
			return;
		}
		const isolatedStyle = this.getIsolatedStyleSheet();
		isolatedStyle.innerHTML = this.props.stylesheet;
		let stylesheet = isolatedStyle.sheet;

		if(!stylesheet) {return null;}

		let inheritableFontSizesString = '';

		_.each(stylesheet.rules, rule => {
			if (	
				rule.style?.fontSize 
				&& rule.style?.fontSize !== ''
			) {
				// calc the font size on the selector that has a declared `font-size`
				let fontSize = rule.style.fontSize;
				let starSelector = rule.selectorText === 'bodycopy' ? ', '+rule.selectorText+' *' : '';
				inheritableFontSizesString += rule.selectorText+' {--font-size: '+fontSize+';}\n'
				inheritableFontSizesString += rule.selectorText+starSelector+' {font-size: calc(var(--font-scale) * var(--font-size));}\n'
			}

		});

		this.setState({inheritableFontSizesString: inheritableFontSizesString});
		
	}

	setMobileOffsets = () => {

		if(helpers.isServer) {
			return;
		}

		if (!this.props.isMobile) {
			this.setState({mobileOffsetsString: null})
			return;
		}

		if (!this.props.stylesheet) {
			return;
		}

		const isolatedStyle = this.getIsolatedStyleSheet();
		isolatedStyle.innerHTML = this.props.stylesheet;

		const string = getMobileOffsetsString(isolatedStyle.sheet, [
			{
				properties: [
					'padding-top',
					'padding-right',
					'padding-bottom',
					'padding-left',
				],
				denyList: [
					'ul',
					'ul.lineated',
					'ol',
					'li',
					'h1',
					'h2',
					'h3',
					'h4',
					'h5',
					'h6',
					'sub',
					'sup',
					'blockquote',
					'small-caps',
					'match:audio-player',
					'match:shop-product',
					'.quick-view',
					'.quick-view .caption',
					'.quick-view .caption-background',
					'.mobile .quick-view',
					'.mobile .quick-view .caption'
				]
			}, {
				properties: [
					'margin-top',
					'margin-right',
					'margin-bottom',
					'margin-left'
				],
				allowList: [
					'.content',
					'.mobile .content'
				]
			}
		])

		this.setState({mobileOffsetsString: string});
	}

	getIsolatedStyleSheet = _.once((css) => {
		const doc = document.implementation.createDocument('http://www.w3.org/1999/xhtml', 'body', null);
		const style = document.createElement('style');
		style.innerHTML = css;
		doc.children[0].appendChild(style);
		return style;
	})

}

function mapReduxStateToProps(state) {

	const canDuplicate = helpers.isServer ? state.site?.is_template : state.site?.is_template && parent === window;

	return {
		hasSitePackage: state.frontendState.hasSitePackage,
		adminInitialized: state.adminState?.initialized || false,
		inAdminFrame: state.frontendState.inAdminFrame,
		isMobile: state.frontendState.isMobile,
		adminMode: state.frontendState.adminMode,
		showCargoLogo: !canDuplicate && state.site?.show_cargo_logo,
		customHTML: state.site?.custom_html,
		access_level: state.site?.access_level,
		website_title: state.site?.website_title,
		domain_active: state.site?.domain_active,
		domain: state.site?.domain,
		site_url: state.site?.site_url,
		site_id: state.site?.id,
		fonts: state.site?.fonts,
		fontsLoaded: state.frontendState.fontsLoaded,
		stylesheet: state.css?.stylesheet,
		hasAuth: state.auth.authenticated,
		canDuplicate: canDuplicate,
		activePID: state.frontendState.activePID,
		itemsInCart: _.values(state.commerce.cart).reduce((acc, item) => acc += (item?.quantity || 0), 0)
	};

}

function mapDispatchToProps(dispatch) {
	
	return bindActionCreators({ 
		fetchSitePackage: actions.fetchSitePackage,
		updateFrontendState: actions.updateFrontendState,
		getAuthToken: actions.getAuthToken
	}, dispatch);

}

export default withStore(connect(
	mapReduxStateToProps, 
	mapDispatchToProps
)(
	frontloadConnect(frontload, {
		onMount: true,
		onUpdate: false
	})(
		App
	)
));